/**
 * `/sales/customers/:customerId/ledger` — customer statement view.
 *
 * Read-only projection over invoices + receipts + credit notes for one
 * customer with a running-balance column. See
 * `packages/domain/src/sales/customers/get-customer-ledger.ts` for the
 * sign convention.
 *
 * V1.0.x scope: no filters, no PDF export, no allocation-level detail.
 * Those land with the receipts vertical (V1.x).
 */
import { createFileRoute } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { CustomerLedgerPage } from "../../../../features/customers/ledger/index.ts";
import "../../../../features/customers/components/customer-detail.css";

export const Route = createFileRoute("/(workspace)/sales/customers/$customerId/ledger")({
  component: CustomerLedgerRoute,
});

function CustomerLedgerRoute() {
  const { customerId } = Route.useParams();
  const customer = trpc.customers.get.useQuery({ id: customerId });
  const ledger = trpc.customers.ledger.useQuery(
    { id: customerId },
    { enabled: customer.data !== undefined },
  );

  const customerName = customer.data?.name ?? "Customer";

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Sales", to: "/" },
        { label: "Customers", to: "/sales/customers" },
        // `Breadcrumbs` renders a plain <a href>, not a router <Link> — a
        // `params` object was ignored, so this linked to the literal
        // "/sales/customers/$customerId" path. Interpolate here instead.
        { label: customerName, to: `/sales/customers/${customerId}` },
        { label: "Ledger" },
      ]}
    >
      {customer.isLoading || ledger.isLoading ? (
        <p className="text-muted">Loading ledger…</p>
      ) : customer.error ? (
        <Alert
          intent="bad"
          title={
            customer.error.data?.code === "NOT_FOUND"
              ? "Customer not found."
              : "Couldn't load customer."
          }
          description={customer.error.message}
        />
      ) : ledger.error ? (
        <Alert
          intent="bad"
          title="Couldn't load ledger."
          description={ledger.error.message}
        />
      ) : customer.data && ledger.data ? (
        <CustomerLedgerPage customer={customer.data} ledger={ledger.data} />
      ) : null}
    </WorkspaceShell>
  );
}
